import type { Order } from '@my/tms-api';
import { useOrderTemplatePreview, useOrderTemplates } from '@my/tms-api/hooks';
import { Loading, cn } from '@my/ui';
import { useState } from 'react';
import { CopyFromOrderPanel, type NewOrderContext } from './CopyFromOrderPanel';
import { OrderTemplatesPanel } from './OrderTemplatesPanel';
import { PreviousOrdersPanel } from './PreviousOrdersPanel';

type Source = 'orders' | 'templates';
const MODE_KEY = 'tms.newOrder.startFrom';

/** The last list the user chose, per browser. */
function rememberedMode(): Source {
  try {
    return localStorage.getItem(MODE_KEY) === 'templates' ? 'templates' : 'orders';
  } catch {
    return 'orders';
  }
}

/**
 * New order, customer picked: their earlier orders — or their order templates
 * — on the left, what would be copied from the one picked on the right. One
 * copy engine behind both. Holds the selection; each side owns its own data.
 */
export function StartFromPreviousOrder({
  organisationId,
  customerPartyId,
  customerName,
  cargoDefinitionId,
  cargoVariantId,
  cargoLabel,
  newOrder,
  templatesEnabled,
}: {
  organisationId: string | null;
  customerPartyId: string;
  customerName: string;
  cargoDefinitionId: string | null;
  cargoVariantId: string | null;
  cargoLabel: string | null;
  /** The New order screen's number, currency and typed references. */
  newOrder: NewOrderContext;
  /** Order templates switched on for the organisation (Copy Functions). */
  templatesEnabled: boolean;
}) {
  const [selected, setSelected] = useState<Order | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [modeChoice, setModeChoice] = useState<Source>(rememberedMode);
  const mode: Source = templatesEnabled ? modeChoice : 'orders';
  const choose = (next: Source) => {
    setModeChoice(next);
    try {
      localStorage.setItem(MODE_KEY, next);
    } catch {
      // A private window: the choice just isn't remembered.
    }
  };

  const templatesQuery = useOrderTemplates(
    templatesEnabled ? organisationId : null,
    customerPartyId,
  );
  const previewQuery = useOrderTemplatePreview(mode === 'templates' ? templateId : null);

  const toggle = templatesEnabled ? (
    <div className="flex shrink-0 rounded-md bg-slate-100 p-0.5 text-xs" role="tablist">
      {(
        [
          ['orders', 'Previous orders'],
          [
            'templates',
            `Templates${templatesQuery.data ? ` (${templatesQuery.data.length})` : ''}`,
          ],
        ] as const
      ).map(([value, label]) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={mode === value}
          onClick={() => choose(value)}
          className={cn(
            'whitespace-nowrap rounded-sm px-3 py-1 font-medium transition-colors',
            mode === value
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
      {/* Side by side, the list is laid over its cell rather than sizing it:
          the panel on the right sets the row's height and the list fills it,
          scrolling within. Sized by its own rows it would grow the row
          instead, and never need to scroll. The minimum keeps a short right
          panel from squashing it. Stacked (narrow screens) it flows normally. */}
      <div className="relative min-w-0 xl:col-span-2 xl:min-h-[24rem]">
        <div className="xl:absolute xl:inset-0">
          {mode === 'templates' ? (
            <OrderTemplatesPanel
              organisationId={organisationId}
              customerPartyId={customerPartyId}
              customerName={customerName}
              cargoDefinitionId={cargoDefinitionId}
              cargoVariantId={cargoVariantId}
              selectedTemplateId={templateId}
              onSelect={setTemplateId}
              headerAside={toggle}
            />
          ) : (
            <PreviousOrdersPanel
              organisationId={organisationId}
              customerPartyId={customerPartyId}
              customerName={customerName}
              cargoDefinitionId={cargoDefinitionId}
              cargoVariantId={cargoVariantId}
              cargoLabel={cargoLabel}
              selectedOrderId={selected?.id ?? null}
              onSelect={setSelected}
              headerAside={toggle}
            />
          )}
        </div>
      </div>
      <div className="min-w-0">
        {mode === 'templates' ? (
          templateId && previewQuery.isPending ? (
            <section className="flex h-full items-center justify-center rounded-md border border-slate-200 bg-white p-4">
              <Loading label="Loading template" />
            </section>
          ) : (
            // Keyed by template, so picking another one starts from fresh choices.
            <CopyFromOrderPanel
              key={`template-${previewQuery.data?.template.id ?? 'none'}`}
              organisationId={organisationId}
              order={templateId ? (previewQuery.data?.order ?? null) : null}
              templatePreview={templateId ? (previewQuery.data ?? null) : null}
              newOrder={newOrder}
            />
          )
        ) : (
          // Keyed by order, so picking another one starts from fresh choices.
          <CopyFromOrderPanel
            key={selected?.id ?? 'none'}
            organisationId={organisationId}
            order={selected}
            newOrder={newOrder}
          />
        )}
      </div>
    </div>
  );
}
